import { useLayoutEffect } from 'react'
import type { ECharts } from 'echarts'

/** Resize the existing canvas before paint so panel drags preserve charts and zoom. */
export function useChartResize(chart: ECharts | null) {
  useLayoutEffect(() => {
    if (!chart || chart.isDisposed()) return
    const element = chart.getDom()
    const resize = () => {
      if (chart.isDisposed()) return
      const width = element.clientWidth
      const height = element.clientHeight
      // A transient hidden container should not clear the last rendered frame.
      if (width <= 0 || height <= 0) return
      if (width === chart.getWidth() && height === chart.getHeight()) return
      chart.resize({ width, height, animation: { duration: 0 } })
      // ECharts resizes its canvas immediately but normally paints on its next
      // animation frame. Flush within the observer to avoid a blank frame.
      chart.getZr().flush()
    }

    resize()
    if (typeof ResizeObserver !== 'undefined') {
      // ResizeObserver batches layout changes before paint; a debounce would
      // leave the chart behind the cursor throughout a continuous panel drag.
      const observer = new ResizeObserver(resize)
      observer.observe(element)
      return () => observer.disconnect()
    }
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [chart])
}
